import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { LockKey, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth-context";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const DEMO_ACCOUNTS = [
  { role: "Super Admin", email: "admin@vidyaseva.org" },
  { role: "Hostel Admin", email: "hostel.admin@vidyaseva.org" },
  { role: "School Admin", email: "school.admin@vidyaseva.org" },
  { role: "Teacher", email: "teacher@vidyaseva.org" },
  { role: "Donor", email: "donor@vidyaseva.org" },
];
const DEMO_PASSWORD = process.env.REACT_APP_DEMO_PASSWORD || "";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const from = location.state?.from?.pathname || "/app";

  // Pre-fill the email when arriving via /login?role=trust|hostel|school|teacher|donor.
  // Lets the landing page route each persona to a tailored login experience.
  const search = new URLSearchParams(location.search);
  const presetRole = search.get("role");
  const PRESETS = {
    trust: { email: "trust.admin@vidyaseva.org", banner: "Trust Admin login — full visibility of your trust's hostels, schools, donors and students." },
    hostel: { email: "hostel.admin@vidyaseva.org", banner: "Hostel Admin login — manage your hostel's students, fees, attendance, mess." },
    school: { email: "school.admin@vidyaseva.org", banner: "School Admin login — academics, fees, marksheets, attendance." },
    teacher: { email: "teacher@vidyaseva.org", banner: "Teacher login — your class, your subjects." },
    donor: { email: "donor@vidyaseva.org", banner: "Donor login — sponsored children's progress, photos and 80G receipts." },
    super: { email: "admin@vidyaseva.org", banner: "Super-admin login — platform/developer access. End-customers should NOT use this." },
  };
  const preset = presetRole && PRESETS[presetRole];

  const [email, setEmail] = useState(preset?.email || "admin@vidyaseva.org");
  const [password, setPassword] = useState(DEMO_PASSWORD);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        // Stash the challenge token in router state and route to the challenge page.
        navigate("/2fa-challenge", { replace: true, state: { challenge_token: result.challenge_token, email: result.email, from } });
        return;
      }
      toast.success(`Welcome, ${result?.name || "user"}!`);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg grid grid-cols-1 lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col px-6 lg:px-16 py-10 relative">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
          <div className="leading-tight">
            <div className="font-display font-semibold">Vidya Seva</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vs-text-secondary)]">Management System</div>
          </div>
        </Link>

        <div className="absolute top-6 right-6 lg:right-auto lg:left-1/2 lg:translate-x-[-50%]">
          <LanguageSwitcher />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="my-auto max-w-md w-full"
        >
          <div className="eyebrow mb-3">{t("login.secure_portal")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            {t("login.title")} <span className="italic font-light text-[var(--vs-secondary)]">{t("login.title_accent")}</span>
          </h1>
          <p className="mt-3 text-sm text-[var(--vs-text-secondary)]">
            {t("login.subtitle")}
          </p>

          {preset && (
            <div
              className={`mt-5 rounded-md border-l-4 p-3 text-xs leading-relaxed ${
                presetRole === "super"
                  ? "border-red-500 bg-red-50 text-red-900"
                  : "border-[var(--vs-secondary)] bg-[var(--vs-secondary)]/5 text-[var(--vs-text)]"
              }`}
              data-testid={`login-role-banner-${presetRole}`}
              role="note"
            >
              <div className="font-mono tracking-widest uppercase text-[10px] mb-1">
                {presetRole === "super" ? "DEVELOPER ACCESS" : `${presetRole.toUpperCase()} LOGIN`}
              </div>
              {preset.banner}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)] mb-1.5">{t("login.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
                placeholder="you@vidyaseva.org"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)]">{t("login.password")}</label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[10px] font-mono uppercase tracking-widest text-[var(--vs-text-secondary)] hover:text-[var(--vs-secondary)] underline underline-offset-2">
                  {t("login.forgot")}
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-[var(--vs-danger)] bg-[var(--vs-danger)]/10 border border-[var(--vs-danger)]/30 rounded-md p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full px-6 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2 font-medium"
            >
              {submitting ? `${t("login.submit")}…` : t("login.submit")}
              {!submitting && <ArrowRight size={16} />}
            </button>

            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[var(--vs-secondary)]">
                <LockKey size={12} weight="fill" /> AES-256
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[var(--vs-secondary)]">
                <ShieldCheck size={12} weight="fill" /> TLS 1.2+
              </div>
            </div>
          </form>
        </motion.div>

        <div className="text-xs text-[var(--vs-text-secondary)]">
          ← <Link to="/" className="underline underline-offset-4 hover:text-[var(--vs-secondary)]">{t("login.back_to_landing")}</Link>
        </div>
      </div>

      {/* Right: demo accounts */}
      <div className="hidden lg:flex bg-[var(--vs-bg-inverse)] text-[var(--vs-text-inverse)] flex-col px-12 py-10">
        <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vs-primary)] mb-4">{t("login.demo_accounts")}</div>
        <h2 className="font-display text-2xl font-semibold tracking-tight leading-tight max-w-md">
          {t("login.demo_subtitle")}
        </h2>
        <div className="mt-8 space-y-2">
          {DEMO_ACCOUNTS.map((d) => (
            <button
              key={d.email}
              type="button"
              data-testid={`demo-${d.role.toLowerCase().replace(" ", "-")}`}
              onClick={() => {
                setEmail(d.email);
                setPassword(DEMO_PASSWORD);
              }}
              className="w-full text-left p-4 rounded-md border border-white/10 hover:border-[var(--vs-primary)] hover:bg-white/5 transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-base font-semibold">{d.role}</div>
                  <div className="text-xs font-mono text-white/65 mt-0.5">{d.email}</div>
                </div>
                <ArrowRight size={16} className="text-[var(--vs-primary)]" />
              </div>
            </button>
          ))}
        </div>
        <div className="mt-auto pt-8 text-xs font-mono text-white/50 flex items-center justify-between gap-3">
          <span>{t("login.demo_password_label")} <span className="text-[var(--vs-primary)]">VidyaSeva@2026</span></span>
          <a href="/about" className="hover:text-[var(--vs-primary)] underline" data-testid="login-about-link">About · Bharat Cores</a>
        </div>
      </div>
    </div>
  );
}
